import {
	Alert,
	Box,
	Button,
	CheckboxInput,
	Col,
	Divider,
	Dropdown,
	Grid,
	HorizontalScrollShadow,
	Icons,
	Inline,
	Input,
	Spinner,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	ErrorMessage,
	Form,
	SubmissionError,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import {
	memo,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { DocTypeXHR, IDocType } from "../DocTypes"
import { generatePath } from "../router-utils"
import { IDocGroup } from "./store"

export function XHR(xhr: XHRInstance) {
	return {
		async get(
			params?: Record<string, unknown>,
			pagination: "simple" | "cursor" | "paginate" = "paginate"
		): Promise<IListResponse<IDocGroup>> {
			return xhr
				.get<
					IListResponse<IDocGroup>
				>("/document-groups", { params: { ...params, pagination } })
				.then(({ data }) => data)
		},
		async create({ name, document_types }: IDocGroup) {
			return xhr
				.post<{
					data: IDocGroup
				}>(`/document-groups/`, {
					name,
					document_types: document_types.map((doc) => doc.id),
				})
				.then(({ data }) => data)
		},
		async update(id: number | string, { name, document_types }: IDocGroup) {
			return xhr
				.patch<{
					data: IDocGroup
				}>(`/document-groups/${id}`, {
					name,
					document_types: document_types.map((doc) => doc.id),
				})
				.then(({ data }) => data)
		},
		async destroy(id: number | string) {
			return xhr
				.delete<{ message: string }>(`/document-groups/${id}`)
				.then((resp) => resp.data)
		},
	}
}

export function DocGroupsList() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch(query)
	const [searchKey, setSearchKey] = useState(0)
	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])
	const { hasPermission } = useCheckPermissions()
	const canAddOrUpdate = hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS)
	const handleUpdateKey = useCallback(() => {
		setSearchKey((prev) => prev + 1)
	}, [])

	return (
		<Search
			key={searchKey}
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			title="Document Groups"
			resetParams={{
				q: "",
				page: 1,
			}}
			actions={
				<>
					{canAddOrUpdate ? (
						<CreateDocumentGroupDialog onRefresh={handleUpdateKey}>
							{({ open }) => (
								<Button onClick={open}>
									<Icons.Plus /> Add Document Group
								</Button>
							)}
						</CreateDocumentGroupDialog>
					) : null}
				</>
			}
		>
			<ListView<IDocGroup>
				pageKey="doc-groups"
				fetch={(xhr, params) => XHR(xhr).get({ ...params })}
				params={params}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items: groups, refresh, xhr }) => (
					<Table
						headers={["Group Name", "Documents", ""]}
						bordered
						hover
						responsive
						rows={groups.map((group) => [
							<Stack gap="px">
								<Text>{group.name}</Text>
							</Stack>,
							<Text>
								{group.document_types.map((doc) => doc.name).join(", ")}
							</Text>,

							!hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS) ? null : (
								<Dropdown alignRight key={group.id}>
									<Dropdown.ToggleButton size="sm" level="tertiary">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<EditDocGroupDialog group={group} onRefresh={refresh}>
											{({ open }) => (
												<Dropdown.MenuItem onClick={open}>
													<Icons.Pencil /> Edit
												</Dropdown.MenuItem>
											)}
										</EditDocGroupDialog>
									</Dropdown.Menu>
								</Dropdown>
							),
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

function CreateDocumentGroupDialog({
	children,
	onRefresh,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	onRefresh: () => void
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({
				open: () => {
					open()
				},
			})}
			<Dialog open={isOpen} onClose={close} lg title="Create Document Group">
				<Dialog.Body>
					<DocGroupForm
						onCancel={close}
						onSuccess={() => {
							close()
							onRefresh()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function EditDocGroupDialog({
	children,
	group,
	onRefresh,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	group: IDocGroup
	onRefresh: () => void
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({
				open: () => {
					open()
				},
			})}
			<Dialog open={isOpen} onClose={close} lg title="Edit Document Group">
				<Dialog.Body>
					<DocGroupForm
						onCancel={close}
						docGroupId={group.id}
						initialValue={group}
						onSuccess={() => {
							close()
							onRefresh()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function DocGroupForm({
	initialValue,
	docGroupId,
	onSuccess,
	onCancel,
}: {
	docGroupId?: number
	initialValue?: IDocGroup
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const validationSchema = validateFormValues(
		Validator.object().shape({
			name: Validator.string()
				.required("Document group name is required")
				.min(2, "Document group name must be greater than one character")
				.max(500, "Document group name must be less than 500 characters"),
			document_types: Validator.array()
				.of(Validator.mixed<IDocType>())
				.required("At least one document is required")
				.min(1, "At least one document is required"),
		})
	)

	return (
		<Form
			validate={validationSchema}
			initialValues={initialValue}
			onSubmit={withServerErrors(async (values) => {
				if (docGroupId) {
					await XHR(xhr).update(docGroupId, values)
					showSnackbar(`Document Group "${values.name}" Updated Successfully`)
				} else {
					await XHR(xhr).create(values)
					showSnackbar(`Document Group "${values.name}" Created Successfully`)
				}
				onSuccess()
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="2">
						<Grid gap="6">
							<Col xl={12} paddingTop="2">
								<DocumentFieldInput name={"document_types"} />
							</Col>
							<Col xl={12}>
								<TextInputField
									label="Group Name"
									name="name"
									type="string"
									placeholder="e.g. Japan "
									min={1}
									max={500}
								/>
							</Col>
						</Grid>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save Details"}
							</Button>
							<Button
								level="tertiary"
								disabled={submitting}
								onClick={() => onCancel()}
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function DocumentFieldInput({ name }: { name: string }) {
	return (
		<Stack gap="8">
			<CustomDocumentList name={name} />
			<SelectedDocuments name={name} />
		</Stack>
	)
}

function asDocumentArray(value: unknown): IDocType[] {
	return Array.isArray(value) ? value : []
}

const PAGE_SIZE = 15

async function docsApi({
	xhr,
	params: { page, per_page, search },
}: {
	params: {
		page: number
		per_page: number
		search: string
	}
	xhr: XHRInstance
}): Promise<IListResponse<IDocType>> {
	const q = search.trim().toLowerCase()
	return DocTypeXHR(xhr).get({
		q,
		per_page,
		page,
		pagination: "page",
		order: "desc",
	})
}

function LabeledContainer({
	name,
	children,
}: {
	name: string
	children: ReactNode
}) {
	return (
		<Box
			position="relative"
			borderRadius="md"
			borderWidth="1"
			borderColor="muted"
		>
			<Inline
				position="absolute"
				borderRadius="lg"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="primary"
				paddingX="2"
				paddingY="1"
				style={{ top: "-15px", left: "16px" }}
			>
				<Text fontWeight="semibold">{name}</Text>
			</Inline>
			{children}
		</Box>
	)
}

function SearchInput({
	value,
	onChange,
}: {
	value: string
	onChange: (value: string) => void
}) {
	return (
		<Inline justifyContent="end" alignItems="center" paddingY="4" paddingX="3">
			<Box
				position="relative"
				width="full"
				maxWidth={{ md: "xs" }}
				marginLeft="auto"
			>
				<Inline
					position="absolute"
					top="0"
					left="0"
					height="full"
					alignItems="center"
					width="10"
					justifyContent="center"
				>
					<Icons.Search color="muted" />
				</Inline>
				<Input
					type="search"
					placeholder="Search....."
					autoFocus={true}
					paddingLeft="10"
					value={value}
					onChange={(e) => {
						onChange(e.currentTarget.value || "")
					}}
				/>
			</Box>
		</Inline>
	)
}

function CustomDocumentList({ name }: Readonly<{ name: string }>) {
	const xhr = useXHR()
	const [search, setSearch] = useState("")
	const debouncedSearch = useDebounce(search.trim(), 500)
	const { value: selectedDocuments, onChange: onDocumentsChange } =
		useFieldValue<IDocType[]>(name)

	const renderItem = useCallback(
		(item: IDocType, isSelected: boolean, onChange: (s: boolean) => void) => {
			const key = item.id + "document_type"
			return (
				<Inline
					as="li"
					key={key}
					alignItems="center"
					gap="2"
					bgColor={{ default: "default", hover: "inset" }}
					paddingX="1"
					rounded="md"
					cursor="pointer"
					onClick={() => onChange(!isSelected)}
					tabIndex={0}
					role="checkbox"
					aria-checked={isSelected}
					onKeyDown={(e) => {
						if (e.key === " " || e.key === "Enter") {
							e.preventDefault()
							onChange(!isSelected)
						}
					}}
				>
					<Stack alignItems="start">
						<CheckboxInput
							htmlFor={key}
							style={{ paddingTop: "4px" }}
							checked={isSelected}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								onChange(e.target.checked)
							}}
							onClick={(e) => e.stopPropagation()} // Prevent parent click triggering twice
						/>
					</Stack>
					<Text fontWeight="semibold" fontSize="base">
						{item.name}
					</Text>
				</Inline>
			)
		},
		[]
	)

	const fetchDocs = useCallback(
		(params: { page: number; per_page: number; search: string }) => {
			return docsApi({ xhr, params })
		},
		[xhr]
	)

	return (
		<LabeledContainer name="List of Documents">
			<SearchInput
				value={search}
				onChange={(value) => {
					setSearch(value)
				}}
			/>
			<PaginatedColumnGrid<IDocType>
				fetch={fetchDocs}
				itemId={(d) => d.id}
				renderItem={renderItem}
				selectedDocuments={asDocumentArray(selectedDocuments)}
				onDocumentsChange={onDocumentsChange}
				search={debouncedSearch}
			/>
			<ErrorMessage name={name} />
		</LabeledContainer>
	)
}

function chunk<T>(arr: T[], size: number): T[][] {
	const out: T[][] = []
	for (let i = 0; i < arr.length; i += size) {
		out.push(arr.slice(i, i + size))
	}
	return out
}

function toggleDocumentSelection(
	selectedDocuments: IDocType[],
	doc: IDocType,
	checked: boolean
): IDocType[] {
	if (checked) {
		if (selectedDocuments.some((d) => d.id === doc.id)) return selectedDocuments
		return [...selectedDocuments, doc]
	}
	return selectedDocuments.filter((d) => d.id !== doc.id)
}

export function ColumnGrid({
	items,
	selectedDocuments,
	onDocumentsChange,
}: {
	items: IDocType[]
	selectedDocuments: IDocType[]
	onDocumentsChange: (documents: IDocType[]) => void
}) {
	const groups = chunk(items, 15)
	const selectedIds = new Set(selectedDocuments.map((d) => d.id))
	return (
		<HorizontalScrollShadow>
			<Inline
				alignItems="stretch"
				gap="2"
				paddingX="4"
				paddingTop="6"
				paddingBottom="8"
			>
				{groups.map((group, i) => (
					<Inline key={i}>
						<Stack as="ul" gap="2" style={{ width: "max-content" }}>
							{group.map((item) => (
								<Box
									as="li"
									key={item.id}
									display="flex"
									alignItems="center"
									gap="2"
								>
									<CheckboxInput
										checked={selectedIds.has(item.id)}
										onChange={(e) => {
											onDocumentsChange(
												toggleDocumentSelection(
													selectedDocuments,
													item,
													e.currentTarget.checked
												)
											)
										}}
									/>
									<Text fontWeight="semibold" fontSize="base">
										{item.name}
									</Text>
								</Box>
							))}
						</Stack>
						<Box borderRightColor="muted" borderRightWidth="2" marginX="6" />
					</Inline>
				))}
			</Inline>
		</HorizontalScrollShadow>
	)
}

export function SelectedDocuments({ name }: { name: string }) {
	const { value: documents } = useFieldValue<IDocType[]>(name)
	return (
		<Stack gap="1">
			<Box
				position="relative"
				padding="3"
				paddingTop="6"
				borderRadius="md"
				borderWidth="1"
				borderColor="muted"
				backgroundColor="subtle"
			>
				<Inline
					position="absolute"
					borderRadius="lg"
					borderWidth="1"
					borderColor="muted"
					backgroundColor="primary"
					paddingX="2"
					paddingY="1"
					style={{ top: "-15px" }}
				>
					<Text fontWeight="semibold">
						{`Selected Documents (${documents.length})`}
					</Text>
				</Inline>
				{documents.length === 0 ? (
					<Stack alignItems="center" paddingBottom="2">
						<Icons.Info size="6" color="muted" />
						<Text color="muted" fontWeight="semibold">
							No Selected Documents
						</Text>
					</Stack>
				) : (
					<Box style={{ maxHeight: "300px", overflowY: "auto" }}>
						<Inline gap="3" flexWrap="wrap">
							{documents.map((doc, index) => (
								<Box
									key={doc.id}
									paddingX="1"
									paddingY="1"
									paddingRight="2"
									borderWidth="1"
									rounded="md"
									backgroundColor="default"
								>
									<Inline gap="2" alignItems="center">
										<Box paddingX="2" paddingY="1" bgColor="inset" rounded="md">
											{index + 1}
										</Box>
										<Text>{doc.name}</Text>
									</Inline>
								</Box>
							))}
						</Inline>
					</Box>
				)}
			</Box>
		</Stack>
	)
}

interface LoadedPage<T> {
	page_num: number
	data: T[]
	error?: string | null
}

export interface PaginatedColumnGridProps<T> {
	fetch: (params: {
		page: number
		per_page: number
		search: string
	}) => Promise<IListResponse<T>>
	pageSize?: number
	search?: string
	renderItem: (
		item: T,
		isSelected: boolean,
		onChange: (selected: boolean) => void
	) => React.ReactNode
	selectedDocuments?: T[]
	onDocumentsChange?: (selected: T[]) => void
	itemId: (item: T) => string | number
	columnWidth?: number
}

const DEFAULT_COLUMN_WIDTH = 288 // matches w-72

export function PaginatedColumnGrid<T>({
	fetch,
	pageSize = PAGE_SIZE,
	search = "",
	renderItem,
	selectedDocuments,
	onDocumentsChange,
	itemId,
	columnWidth = DEFAULT_COLUMN_WIDTH,
}: PaginatedColumnGridProps<T>) {
	const [pages, setPages] = useState<LoadedPage<T>[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [lastPage, setLastPage] = useState<number | null>(null)

	const scrollContainerRef = useRef<HTMLDivElement | null>(null)
	const sentinelRef = useRef<HTMLDivElement | null>(null)
	const inFlightRef = useRef<Set<number>>(new Set())
	const mountedRef = useRef(true)
	const currentQueryIdRef = useRef(0)

	const [internalSelected, setInternalSelected] = useState<T[]>([])
	const selected = selectedDocuments ?? internalSelected
	const selectedIds = useMemo(
		() => new Set(selected.map((s) => itemId(s))),
		[selected, itemId]
	)

	const updateSelected = useCallback(
		(next: T[]) => {
			if (onDocumentsChange) onDocumentsChange(next)
			if (!selectedDocuments) setInternalSelected(next)
		},
		[onDocumentsChange, selectedDocuments]
	)

	const hasMore = lastPage === null ? true : pages.length < lastPage

	const fetchPage = useCallback(
		async (page: number, queryId: number) => {
			if (inFlightRef.current.has(page)) return
			inFlightRef.current.add(page)
			try {
				const res = await fetch({
					page,
					per_page: pageSize,
					search,
				})
				if (!mountedRef.current || currentQueryIdRef.current !== queryId) return
				setLastPage(res.meta.last_page)
				setPages((prev) => {
					if (prev.some((p) => p.page_num === page)) return prev
					const next = [...prev, { page_num: page, data: res.data }]
					next.sort((a, b) => a.page_num - b.page_num)
					return next
				})
			} catch (e) {
				if (!mountedRef.current || currentQueryIdRef.current !== queryId) return
				const msg = e instanceof Error ? e.message : "Failed to load"
				setPages((prev) => {
					if (prev.some((p) => p.page_num === page)) return prev
					const next = [...prev, { page_num: page, data: [], error: msg }]
					next.sort((a, b) => a.page_num - b.page_num)
					return next
				})
			} finally {
				inFlightRef.current.delete(page)
			}
		},
		[fetch, pageSize, search]
	)

	// Initial fill / reset on search or pageSize change
	useEffect(() => {
		mountedRef.current = true
		const queryId = ++currentQueryIdRef.current

		// reset state when the query changes
		setPages([])
		setLastPage(null)
		inFlightRef.current.clear()
		if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft = 0

		const container = scrollContainerRef.current
		const viewportWidth = container?.clientWidth ?? window.innerWidth
		const cols = Math.max(1, Math.ceil(viewportWidth / columnWidth))
		setIsLoading(true)

		// Fetch page 1 first to get lastPage, then fetch remaining pages up to that limit
		fetchPage(1, queryId)
			.then(async () => {
				if (!mountedRef.current || currentQueryIdRef.current !== queryId) return
				// Fetch remaining pages (2 to cols), but fetchPage will check against lastPage
				const pagesToFetch = Array.from(
					{ length: Math.max(0, cols - 1) },
					(_, i) => fetchPage(i + 2, queryId)
				)
				if (pagesToFetch.length > 0) {
					await Promise.all(pagesToFetch)
				}
			})
			.finally(() => {
				if (mountedRef.current && currentQueryIdRef.current === queryId) {
					setIsLoading(false)
				}
			})

		return () => {
			mountedRef.current = false
		}
	}, [fetch, columnWidth, search, pageSize, fetchPage])

	// IntersectionObserver on sentinel for next page
	useEffect(() => {
		const sentinel = sentinelRef.current
		const root = scrollContainerRef.current
		if (!sentinel || !root) return
		if (!hasMore) return

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && hasMore && !isLoading) {
						const nextPage = pages.length + 1
						if (lastPage !== null && nextPage > lastPage) return
						if (inFlightRef.current.has(nextPage)) return
						const queryId = currentQueryIdRef.current
						setIsLoading(true)
						fetchPage(nextPage, queryId).finally(() => {
							if (mountedRef.current && currentQueryIdRef.current === queryId) {
								setIsLoading(false)
							}
						})
					}
				}
			},
			{ root, rootMargin: "0px 200px 0px 0px", threshold: 1 }
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [pages.length, hasMore, isLoading, lastPage, fetchPage])

	const toggleId = useCallback(
		(item: T, checked: boolean) => {
			const id = itemId(item)
			if (checked) {
				if (selectedIds.has(id)) return
				updateSelected([...selected, item])
			} else {
				updateSelected(selected.filter((s) => itemId(s) !== id))
			}
		},
		[selected, selectedIds, updateSelected, itemId]
	)

	const isEmpty =
		!isLoading && pages.length > 0 && pages.every((p) => p.data.length === 0)

	const renderedColumns = useMemo(
		() =>
			pages.map((page) => (
				<Column
					key={page.page_num}
					pageNum={page.page_num}
					error={page.error}
					lastPage={lastPage ?? 1}
				>
					{page.data.map((item) => {
						const id = itemId(item)
						const isSelected = selectedIds.has(id)
						return (
							<ColumnItem
								key={id}
								item={item}
								isSelected={isSelected}
								onChange={(checked) => toggleId(item, checked)}
								renderItem={renderItem}
							/>
						)
					})}
				</Column>
			)),
		[pages, selectedIds, itemId, renderItem, toggleId, lastPage]
	)

	return (
		<Stack
			borderTopWidth="1"
			borderTopColor="muted"
			paddingTop="1"
			paddingBottom={isEmpty ? "0" : "6"}
			paddingLeft={isEmpty ? "0" : "6"}
		>
			<Inline
				ref={scrollContainerRef}
				position="relative"
				height="full"
				width="full"
				overflowX="auto"
				overflowY="hidden"
				bgColor="default"
			>
				{renderedColumns}
				{hasMore && (
					<Stack justifyContent="center" alignItems="center">
						<Inline
							ref={sentinelRef}
							alignItems="center"
							justifyContent="center"
							gap="2"
							color="muted"
							flexShrink="0"
							style={{
								width: "max-content",
							}}
						>
							<Spinner />
							<Text textAlign="center" as="i">
								{isLoading ? "Loading next page…" : "Scroll for more"}
							</Text>
						</Inline>
					</Stack>
				)}
			</Inline>
			{isEmpty && (
				<Box padding="4">
					<Alert
						status="warning"
						title="Can't find the document you're looking for?"
					>
						<Stack gap="2">
							<Text>
								The document may not be added to your organisation's Document
								Types list, or it may not be linked to the selected destination.
							</Text>
							<Text>
								Please verify the document type and destination mapping here:{" "}
								<Link
									to={generatePath("/org/document/types")}
									color="accent"
									fontWeight="semibold"
									target="_blank"
								>
									Link <Icons.ArrowTopRightOnSquare />
								</Link>
							</Text>
						</Stack>
					</Alert>
				</Box>
			)}
		</Stack>
	)
}

interface ColumnProps {
	pageNum: number
	children: ReactNode
	error?: string | null
	isLoading?: boolean
	lastPage: number
}

export const Column = ({
	children,
	error,
	isLoading,
	pageNum,
	lastPage,
}: ColumnProps) => {
	return (
		<Stack
			flexShrink="0"
			height="full"
			style={{
				height: "100%",
				width: "max-contant",
				flexShrink: 0,
			}}
		>
			<Box flex="1" overflowY="auto" padding="2">
				{error ? (
					<Box style={{ width: "250px", height: "100%" }}>
						<Alert status="error">{error + "this is the error message"}</Alert>
					</Box>
				) : (
					<Inline gap="2">
						<Stack gap="2" style={{ width: "max-content" }}>
							{children}
						</Stack>
						{pageNum !== lastPage ? (
							<Box borderRightColor="muted" borderRightWidth="1" marginX="6" />
						) : (
							<Box marginX="6" />
						)}
					</Inline>
				)}
			</Box>
		</Stack>
	)
}

interface ColumnItemProps<T> {
	item: T
	isSelected: boolean
	onChange: (selected: boolean) => void
	renderItem: (
		item: T,
		isSelected: boolean,
		onChange: (selected: boolean) => void
	) => React.ReactNode
}

function ColumnItemInner<T>({
	item,
	isSelected,
	onChange,
	renderItem,
}: ColumnItemProps<T>) {
	return <>{renderItem(item, isSelected, onChange)}</>
}

export const ColumnItem = memo(ColumnItemInner) as typeof ColumnItemInner

function useDebounce<T>(value: T, delay = 350): T {
	const [debounced, setDebounced] = useState(value)
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay)
		return () => clearTimeout(t)
	}, [value, delay])
	return debounced
}
